import React, { useState, useEffect, useMemo } from "react";
import { 
  Check, 
  X, 
  Edit3, 
  Activity, 
  DollarSign, 
  AlertCircle, 
  Info, 
  ChevronRight, 
  Eye, 
  FolderOpen,
  ArrowRight,
  TrendingDown,
  Trash2
} from "lucide-react";
import { pb } from "../services/pocketbase";
import { loteService } from "../services/loteService";
import { suppliesService } from "../services/suppliesService";
import LoteSummary from "../components/production/LoteSummary";

// MAPEAMENTO DE STATUS PARA ANÁLISE DE REGRESSÃO
const STATUS_ORDER = {
  "pe_solicitado": 0,
  "pe_imprimindo": 1,
  "pe_cura": 2,
  "pe_fundicao": 3,
  "pe_fundidos": 4,
  "pe_pre_escapulario": 5,
  "pe_conferencia": 6,
  "pe_acabamento": 7,
  "pe_cravacao": 8,
  "pe_polimento": 9,
  "pe_revisao": 10,
  "pe_finalizacao": 11,
  "in_stock": 12
};

export default function LotesTab({ 
  user, 
  inventory = [], 
  bomsList = [], 
  suppliesList = [],
  findCatalogItem
}) {
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLote, setSelectedLote] = useState(null);
  
  // Modals / Edit States
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editItens, setEditItens] = useState([]);
  const [editObs, setEditObs] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [activeRejectLoteId, setActiveRejectLoteId] = useState(null);

  // Permissões
  const isGestorOrMaster = user?.role === "master" || user?.role === "gestor_estoque";

  const loadLotes = async () => {
    try {
      setLoading(true);
      const list = await loteService.getLotes();
      setLotes(list);
    } catch (e) {
      console.error("Erro ao carregar lotes:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLotes();
  }, []);

  // Filtra itens de inventário reais por lote
  const getLoteItems = (loteId) => {
    return inventory.filter(item => item.lote === loteId);
  };

  // Cálculo de progresso de um lote
  const calculateLoteProgress = (loteItems) => {
    const total = loteItems.length;
    if (total === 0) return { percent: 0, concluidas: 0, interceptadas: 0, suspensas: 0, ativas: 0 };
    
    const concluidas = loteItems.filter(i => i.status === "in_stock" || i.situacao_fluxo === "concluido").length;
    const interceptadas = loteItems.filter(i => i.situacao_fluxo === "interceptado").length;
    const suspensas = loteItems.filter(i => i.situacao_fluxo === "suspenso").length;
    const denominator = total - interceptadas - suspensas;
    const percent = denominator <= 0 ? 0 : Math.round((concluidas / denominator) * 100);
    const ativas = denominator - concluidas;

    return { percent, concluidas, interceptadas, suspensas, total, ativas };
  };

  // Cálculo de Regressões no Lote (Item que volta para Revisão ou Solicitado)
  const calculateLoteRegressions = (loteItems) => {
    let totalRegressions = 0;
    const pieceDetails = loteItems.map(item => {
      const history = Array.isArray(item.history) ? item.history : [];
      let itemRegressions = 0;
      history.forEach(evt => {
        const toVal = evt.to || "";
        const textVal = evt.text || "";
        if (
          toVal === "pe_revisao" || 
          toVal === "pe_solicitado" || 
          toVal === "revisao" || 
          toVal === "solicitado" ||
          textVal.includes("pe_revisao") || 
          textVal.includes("pe_solicitado") || 
          textVal.includes("revisão") || 
          textVal.includes("solicitado")
        ) {
          itemRegressions++;
        }
      });
      totalRegressions += itemRegressions;
      return { sku: item.sku, id: item.id, regressions: itemRegressions };
    });

    return { total: totalRegressions, pieces: pieceDetails.sort((a,b) => b.regressions - a.regressions) };
  };

  // Contagem de passagens de status por peça no lote
  const getStatusPassCounts = (item) => {
    const history = Array.isArray(item.history) ? item.history : [];
    const counts = {};
    history.forEach(evt => {
      if (evt.to) {
        counts[evt.to] = (counts[evt.to] || 0) + 1;
      }
    });
    return counts;
  };

  // Cálculo de Custo BOM de Insumo por SKU
  const getSkuBomDetails = (sku) => {
    const cleanSku = sku.toUpperCase().trim();
    const recipe = bomsList.filter(b => b.sku.toUpperCase().trim() === cleanSku);
    
    let expectedCost = 0;
    const details = recipe.map(item => {
      const supply = suppliesList.find(s => s.id === item.supply_id);
      const unitCost = supply ? suppliesService.getSupplyUnitCost(supply) : 0;
      const qty = Number(item.quantity_per_unit) || 0;
      const itemCost = qty * unitCost;
      expectedCost += itemCost;
      return {
        supply_name: supply?.name || "Insumo Desconhecido",
        qty,
        unitCost,
        totalCost: itemCost
      };
    });

    return { expectedCost, details };
  };

  // Calcular Custos Totais de um Lote (Previsto vs Efetivo)
  const calculateLoteCosts = (lote) => {
    let totalExpected = 0;
    const itemsPlanejados = Array.isArray(lote.itens_planejados) 
      ? lote.itens_planejados 
      : JSON.parse(lote.itens_planejados || "[]");

    itemsPlanejados.forEach(it => {
      const { expectedCost } = getSkuBomDetails(it.sku);
      totalExpected += expectedCost * (Number(it.qtd) || 0);
    });

    // Custo efetivo considera apenas peças do lote que não estão suspensas
    const loteItems = getLoteItems(lote.id);
    let totalEffective = 0;
    loteItems.forEach(item => {
      if (item.situacao_fluxo !== "suspenso") {
        const { expectedCost } = getSkuBomDetails(item.sku);
        totalEffective += expectedCost; // 1 unidade por item físico
      }
    });

    return { expected: totalExpected, effective: totalEffective };
  };

  // Quickview de disponibilidade do SKU
  const getSkuQuickview = (sku) => {
    // 1. Estoque real pronto
    const readyQty = inventory.filter(i => i.sku === sku && i.status === "in_stock" && !i.isPE).length;
    // 2. Vindo em outros lotes ativos (excluindo o atual se aplicável, mas mostra geral)
    const pipelineQty = inventory.filter(i => i.sku === sku && i.isPE && i.status !== "pe_interceptado" && i.situacao_fluxo === "ativo").length;
    return { readyQty, pipelineQty };
  };

  // Handlers para Ações do Lote
  const handleApprove = async (loteId) => {
    if (!window.confirm("Deseja realmente aprovar este lote e iniciar a produção? Os insumos serão debitados.")) return;
    try {
      setLoading(true);
      await loteService.approveLote(loteId, user);
      alert("Lote aprovado e enviado para a produção!");
      loadLotes();
    } catch (e) {
      alert("Erro ao aprovar lote: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    if (!rejectReason.trim()) return alert("Insira o motivo da rejeição.");
    try {
      setLoading(true);
      await loteService.rejectLote(activeRejectLoteId, user, rejectReason);
      alert("Lote rejeitado/cancelado!");
      setIsEditModalOpen(false);
      setRejectReason("");
      setActiveRejectLoteId(null);
      loadLotes();
    } catch (e) {
      alert("Erro ao rejeitar lote: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = (lote) => {
    const items = Array.isArray(lote.itens_planejados) 
      ? lote.itens_planejados 
      : JSON.parse(lote.itens_planejados || "[]");
    setEditItens([...items]);
    setEditObs(lote.observacoes || "");
    setSelectedLote(lote);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      await loteService.updateDraftItens(selectedLote.id, editItens, editObs);
      alert("Lote rascunho atualizado!");
      setIsEditModalOpen(false);
      loadLotes();
    } catch (e) {
      alert("Erro ao atualizar lote: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDraft = async (loteId) => {
    if (!window.confirm("Deseja deletar permanentemente este lote rascunho?")) return;
    try {
      setLoading(true);
      await loteService.deleteDraftLote(loteId);
      alert("Lote excluído.");
      loadLotes();
    } catch (e) {
      alert("Erro ao excluir lote: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  // Mapeador de cores de origem de Lote
  const getOrigemStyle = (origem) => {
    switch (origem) {
      case "planejamento":
        return "bg-emerald-100 text-emerald-800 border-emerald-300";
      case "solicitacao_oficina":
        return "bg-amber-100 text-amber-800 border-amber-300";
      default:
        return "bg-blue-100 text-blue-800 border-blue-300";
    }
  };

  // Lotes em buffer e lotes ativos
  const bufferLotes = lotes.filter(l => l.status === "aguardando_aprovacao");
  const ativosLotes = lotes.filter(l => l.status === "em_producao");
  const rascunhoLotes = lotes.filter(l => l.status === "rascunho");

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-8 bg-slate-50">
      
      {/* 1. SEÇÃO BUFFER DE APROVAÇÃO */}
      {isGestorOrMaster && bufferLotes.length > 0 && (
        <section className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h2 className="text-sm font-black text-slate-800 uppercase tracking-wider flex items-center gap-2 mb-4">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse"></span>
            Buffer de Aprovação ({bufferLotes.length} solicitações)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {bufferLotes.map(lote => {
              const itens = Array.isArray(lote.itens_planejados) 
                ? lote.itens_planejados 
                : JSON.parse(lote.itens_planejados || "[]");
              return (
                <div key={lote.id} className="border border-slate-200 rounded-lg p-4 bg-slate-50/50 hover:bg-slate-50 transition-all flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-mono font-bold text-slate-500">{lote.codigo}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded border font-semibold bg-amber-50 text-amber-700 border-amber-200">
                        Aguardando Aprovação
                      </span>
                    </div>
                    
                    <div className="mb-3">
                      <span className="text-[10px] font-bold text-slate-400 uppercase">Itens Solicitados:</span>
                      <div className="space-y-1 mt-1">
                        {itens.map((it, idx) => (
                          <div key={idx} className="flex justify-between items-center bg-white border border-slate-200 p-1.5 rounded text-xs">
                            <span className="font-bold text-slate-700">{it.sku}</span>
                            <span className="bg-purple-100 text-purple-700 font-bold px-2 py-0.5 rounded-md">{it.qtd} pçs</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {lote.motivo_solicitacao && (
                      <div className="bg-amber-50 border border-amber-100 p-2.5 rounded text-xs mb-3">
                        <span className="font-extrabold text-amber-800">Motivo:</span> {lote.motivo_solicitacao}
                      </div>
                    )}
                    
                    {lote.observacoes && (
                      <p className="text-[11px] text-slate-500 italic mb-3">Obs: {lote.observacoes}</p>
                    )}
                  </div>

                  <div className="flex gap-2 border-t border-slate-200 pt-3 mt-2">
                    <button
                      onClick={() => handleApprove(lote.id)}
                      className="flex-1 flex justify-center items-center gap-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-1.5 px-3 rounded text-xs shadow-sm transition-colors"
                    >
                      <Check size={14} /> Aprovar
                    </button>
                    <button
                      onClick={() => {
                        setActiveRejectLoteId(lote.id);
                        setIsEditModalOpen(true);
                      }}
                      className="flex-1 flex justify-center items-center gap-1 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 font-bold py-1.5 px-3 rounded text-xs transition-colors"
                    >
                      <X size={14} /> Rejeitar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* 2. SEÇÃO LOTES ATIVOS */}
      <section className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <h2 className="text-sm font-black text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Activity size={16} className="text-purple-600" />
          Lotes Ativos em Produção ({ativosLotes.length})
        </h2>
        
        {ativosLotes.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <FolderOpen size={36} className="mx-auto mb-2 opacity-50" />
            Nenhum lote em produção no momento.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ativosLotes.map(lote => {
              const loteItems = getLoteItems(lote.id);
              const progress = calculateLoteProgress(loteItems);
              const regressions = calculateLoteRegressions(loteItems);
              const costs = calculateLoteCosts(lote);

              return (
                <div 
                  key={lote.id} 
                  className="border border-slate-200 hover:border-purple-300 rounded-xl p-5 bg-white shadow-sm hover:shadow-md transition-all flex flex-col justify-between cursor-pointer"
                  onClick={() => setSelectedLote(lote)}
                >
                  <div>
                    {/* Header */}
                    <div className="flex justify-between items-center mb-3">
                      <div>
                        <span className="text-xs font-mono font-bold bg-slate-100 text-slate-700 px-2.5 py-1 rounded">
                          {lote.codigo}
                        </span>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${getOrigemStyle(lote.origem)}`}>
                        {lote.origem === "solicitacao_oficina" ? "Oficina" : lote.origem === "planejamento" ? "Planejador" : "Escritório"}
                      </span>
                    </div>

                    <LoteSummary 
                      progress={progress} 
                      costs={costs} 
                      regressions={regressions} 
                      observacoes={lote.observacoes} 
                    />
                  </div>

                  <div className="border-t border-slate-100 pt-3 mt-3 flex justify-between items-center text-xs font-bold text-purple-600 hover:text-purple-700">
                    <span className="flex items-center gap-1">
                      <Eye size={14} /> Detalhar Lote
                    </span>
                    <ChevronRight size={14} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* 3. SEÇÃO RASCUNHOS (PLANEJAMENTO/ESCRITÓRIO NÃO LANÇADOS) */}
      <section className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <h2 className="text-sm font-black text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-2">
          <FolderOpen size={16} className="text-slate-500" />
          Lotes Rascunho ({rascunhoLotes.length})
        </h2>
        
        {rascunhoLotes.length === 0 ? (
          <div className="text-center py-4 text-slate-400 text-xs">
            Nenhum lote em rascunho.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rascunhoLotes.map(lote => {
              const itens = Array.isArray(lote.itens_planejados) 
                ? lote.itens_planejados 
                : JSON.parse(lote.itens_planejados || "[]");
              return (
                <div key={lote.id} className="border border-slate-200 rounded-lg p-4 bg-slate-50/30 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-mono font-bold text-slate-500">{lote.codigo}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded border border-slate-200 font-semibold bg-slate-100 text-slate-600">
                        Rascunho
                      </span>
                    </div>

                    <div className="space-y-1 mb-3">
                      {itens.map((it, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-white border border-slate-100 p-1.5 rounded text-xs">
                          <span className="font-bold text-slate-600">{it.sku}</span>
                          <span className="text-slate-500 font-bold">{it.qtd} un</span>
                        </div>
                      ))}
                    </div>

                    {lote.observacoes && (
                      <p className="text-[11px] text-slate-400 italic mb-2">Obs: {lote.observacoes}</p>
                    )}
                  </div>

                  <div className="flex gap-2 border-t border-slate-100 pt-3 mt-2">
                    <button
                      onClick={() => handleApprove(lote.id)}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-1.5 rounded text-xs transition-colors"
                    >
                      Lançar Lote
                    </button>
                    <button
                      onClick={() => handleOpenEditModal(lote)}
                      className="p-1.5 border border-slate-300 rounded hover:bg-slate-50 text-slate-600"
                      title="Editar"
                    >
                      <Edit3 size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteDraft(lote.id)}
                      className="p-1.5 border border-red-200 hover:bg-red-50 text-red-600 rounded"
                      title="Excluir"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* 4. MODAL DETALHE DO LOTE COMPLETO */}
      {selectedLote && selectedLote.status === "em_producao" && (() => {
        const loteItems = getLoteItems(selectedLote.id);
        const progress = calculateLoteProgress(loteItems);
        const regressions = calculateLoteRegressions(loteItems);
        const costs = calculateLoteCosts(selectedLote);
        const itensPlanejados = Array.isArray(selectedLote.itens_planejados) 
          ? selectedLote.itens_planejados 
          : JSON.parse(selectedLote.itens_planejados || "[]");

        return (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-4xl rounded-xl shadow-2xl border border-slate-200 flex flex-col max-h-[85vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
              
              {/* Modal Header */}
              <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-black text-slate-800">{selectedLote.codigo}</h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded border font-bold uppercase ${getOrigemStyle(selectedLote.origem)}`}>
                      {selectedLote.origem}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400">Criado em {new Date(selectedLote.created).toLocaleDateString()}</p>
                </div>
                <button 
                  onClick={() => setSelectedLote(null)} 
                  className="p-1.5 hover:bg-slate-200 rounded text-slate-500 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                
                {/* 1. Visão Geral / Progresso */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="border border-slate-200 p-4 rounded-lg space-y-2">
                    <span className="text-[10px] text-slate-400 font-black uppercase">Progresso Conclusão</span>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-black text-slate-800">{progress.percent}%</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-2">
                        <div className="bg-purple-600 h-2 rounded-full" style={{ width: `${progress.percent}%` }}></div>
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-500 font-semibold">
                      {progress.concluidas} concluídas · {progress.ativas} ativas · {progress.total} total
                    </p>
                  </div>
                  
                  <div className="border border-slate-200 p-4 rounded-lg space-y-2">
                    <span className="text-[10px] text-slate-400 font-black uppercase">Custo de Materiais (BOM)</span>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-black text-slate-800">R$ {costs.effective.toFixed(2)}</span>
                      <span className="text-xs text-slate-400 font-bold">Consumido</span>
                    </div>
                    <p className="text-[10px] text-slate-500 font-semibold">
                      Custo Previsto Lote: R$ {costs.expected.toFixed(2)}
                    </p>
                  </div>

                  <div className="border border-slate-200 p-4 rounded-lg space-y-2">
                    <span className="text-[10px] text-slate-400 font-black uppercase">Análise de Qualidade / Regressões</span>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-black text-red-600">{regressions.total}</span>
                      <span className="text-xs text-slate-500 font-bold">Retornos de Etapa</span>
                    </div>
                    <p className="text-[10px] text-slate-500 font-semibold">
                      Taxa de retrabalho do lote
                    </p>
                  </div>
                </div>

                {/* 2. Lista de Peças e Quickview de Estoque */}
                <div className="space-y-3">
                  <h4 className="text-xs font-black text-slate-700 uppercase tracking-wider">Itens e Disponibilidade no Lote</h4>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-slate-400 font-bold">
                          <th className="p-3">SKU</th>
                          <th className="p-3">Qtd Lote</th>
                          <th className="p-3">Pronto em Estoque</th>
                          <th className="p-3">Vindo em Outros Lotes</th>
                          <th className="p-3 text-right">Custo BOM / un</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {itensPlanejados.map((it, idx) => {
                          const qv = getSkuQuickview(it.sku);
                          const bom = getSkuBomDetails(it.sku);
                          return (
                            <tr key={idx} className="hover:bg-slate-50/50">
                              <td className="p-3 font-bold text-slate-800">{it.sku}</td>
                              <td className="p-3 font-semibold text-slate-600">{it.qtd} un</td>
                              <td className="p-3">
                                <span className={`px-2 py-0.5 rounded font-bold ${qv.readyQty > 0 ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                                  {qv.readyQty} un
                                </span>
                              </td>
                              <td className="p-3 font-medium text-slate-500">{qv.pipelineQty} un em trânsito</td>
                              <td className="p-3 text-right font-bold text-slate-700">R$ {bom.expectedCost.toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 3. Peças Problemáticas (Regressões) */}
                {regressions.total > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border border-red-100 rounded-lg p-4 bg-red-50/30">
                      <h4 className="text-xs font-black text-red-800 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <TrendingDown size={14} /> Peças com Mais Regressões (Retrabalho)
                      </h4>
                      <div className="space-y-2">
                        {regressions.pieces.slice(0, 5).filter(p => p.regressions > 0).map((p, idx) => {
                          const item = loteItems.find(li => li.id === p.id);
                          const statusLabel = item ? (item.reason?.startsWith("pe_") ? item.reason : item.status) : "desconhecido";
                          return (
                            <div key={idx} className="flex justify-between items-center bg-white border border-red-200/50 p-2 rounded text-xs shadow-sm">
                              <div>
                                <span className="font-bold text-slate-700">{p.sku}</span>
                                <span className="text-[10px] text-slate-400 block">ID: {p.id.substr(-6)} · Etapa: {statusLabel}</span>
                              </div>
                              <span className="bg-red-100 text-red-700 font-extrabold px-2 py-0.5 rounded-full text-[10px]">
                                {p.regressions} regressões
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/30">
                      <h4 className="text-xs font-black text-slate-700 uppercase tracking-wider mb-2">Visão Geral de Transições de Peças</h4>
                      <p className="text-[11px] text-slate-500">
                        O algoritmo varreu o histórico e listou as peças que voltaram etapas no pipeline de fabricação. Isso ajuda a mapear gargalos operacionais específicos (como defeitos de fundição ou falhas de cravação).
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end">
                <button 
                  onClick={() => setSelectedLote(null)} 
                  className="bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold py-2 px-5 rounded-lg text-xs transition-colors"
                >
                  Fechar
                </button>
              </div>

            </div>
          </div>
        );
      })()}

      {/* 5. MODAL EDICAO/REJEICAO LOTE */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl border border-slate-200 p-6 flex flex-col">
            
            {activeRejectLoteId ? (
              // FORMULÁRIO DE REJEIÇÃO
              <form onSubmit={handleReject} className="space-y-4">
                <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider">Rejeitar Lote</h3>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Motivo da Rejeição (Obrigatório):</label>
                  <textarea
                    rows="3"
                    className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Ex: SKU incorreto cadastrado pela oficina, ou falta de insumos na matriz para este lote..."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    required
                  ></textarea>
                </div>
                <div className="flex gap-2 justify-end pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setActiveRejectLoteId(null);
                      setRejectReason("");
                    }}
                    className="bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold py-2 px-4 rounded text-xs"
                  >
                    Voltar
                  </button>
                  <button
                    type="submit"
                    className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded text-xs shadow-sm"
                  >
                    Confirmar Rejeição
                  </button>
                </div>
              </form>
            ) : (
              // FORMULÁRIO DE EDIÇÃO DE RASCUNHO
              <div className="space-y-4">
                <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider">Editar Itens do Lote</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {editItens.map((it, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <input
                        className="flex-1 border border-slate-300 rounded p-2 text-xs uppercase"
                        value={it.sku}
                        onChange={(e) => {
                          const updated = [...editItens];
                          updated[idx].sku = e.target.value;
                          setEditItens(updated);
                        }}
                      />
                      <input
                        type="number"
                        min="1"
                        className="w-20 border border-slate-300 rounded p-2 text-xs text-center font-bold"
                        value={it.qtd}
                        onChange={(e) => {
                          const updated = [...editItens];
                          updated[idx].qtd = e.target.value;
                          setEditItens(updated);
                        }}
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Observações:</label>
                  <input
                    className="w-full border border-slate-300 rounded p-2 text-xs"
                    value={editObs}
                    onChange={(e) => setEditObs(e.target.value)}
                  />
                </div>
                <div className="flex gap-2 justify-end pt-2">
                  <button
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setSelectedLote(null);
                    }}
                    className="bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold py-2 px-4 rounded text-xs"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded text-xs shadow-sm"
                  >
                    Salvar Alterações
                  </button>
                </div>
              </div>
            )}
            
          </div>
        </div>
      )}

    </div>
  );
}
